import { CascoPackage } from '@insurance/api/requests'
import { InputDatePicker } from '@insurance/components'
import { useValidationsField } from '@insurance/hooks'
import { InputParsedNumber, Text } from '@shared/components'
import { getAge } from '@shared/utils'
import { Col, Flex, Form, Input, Row } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'

import { EditPersonsWrapper } from '../PersonsFields'
import { useModifyDriversInfo } from './hooks'
import { CascoFormValues } from './type'
import {
  formProps,
  MAX_DRIVERS_COUNT,
  MIN_AGE,
  MIN_MINI_DRIVER_EXPERIENCE,
  rangeDriverAge,
  rangeDriverExperience
} from './utils'

const DatePickerWrapper = ({
  value,
  disabled,
  ...props
}: {
  value?: Dayjs
  disabled?: boolean
}) => {
  const { t } = useTranslation()

  return (
    <InputDatePicker
      value={value}
      maxDate={dayjs().subtract(MIN_AGE, 'year')}
      disabled={disabled}
      {...(value && {
        suffixIcon: <Text>{t(rangeDriverAge(getAge(value)))}</Text>
      })}
      {...props}
    />
  )
}

const ExperienceWrapper = ({
  value,
  disabled,
  ...props
}: {
  value?: number
  disabled?: boolean
}) => {
  const { t } = useTranslation()

  return (
    <InputParsedNumber
      className="w-full"
      placeholder={t('driving_internship')}
      min={1}
      suffix={t('years')}
      value={value}
      disabled={disabled}
      {...(value && {
        suffix: t(rangeDriverExperience(value))
      })}
      {...props}
    />
  )
}

export const DriversList = () => {
  const { t } = useTranslation()
  const { driversProps, fiftyFiftyFullProps, insurancePackageProps } =
    formProps(t)
  const { fullName, required, minLength } = useValidationsField()

  const drivers = Form.useWatch<CascoFormValues['drivers']>(driversProps.name)
  const fiftyFiftyFull = Form.useWatch(fiftyFiftyFullProps.name, {
    preserve: true
  })
  const insurancePackage = Form.useWatch<CascoPackage>(
    insurancePackageProps.name,
    { preserve: true }
  )

  const isMini = insurancePackage === CascoPackage.MINI

  const showAddAction =
    (drivers?.length || 0) < MAX_DRIVERS_COUNT && !fiftyFiftyFull

  useModifyDriversInfo()

  return (
    <Flex vertical gap={12}>
      <Text strong size={16}>
        {t('max_drivers_count', { count: MAX_DRIVERS_COUNT })}
      </Text>

      <EditPersonsWrapper
        personsKey={driversProps.name}
        showAddAction={showAddAction}
        showDeleteAction={() => true}
        collapseTitle={(field) =>
          t('driver_number', { number: field.name + 1 })
        }
      >
        {(field, persons) => (
          <Row gutter={12}>
            <Col xs={24} lg={12}>
              <Form.Item
                label={t('last_name')}
                name={[field.name, 'last_name']}
                rules={[required, fullName]}
              >
                <Input placeholder={t('last_name')} />
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item
                label={t('first_name')}
                name={[field.name, 'first_name']}
                rules={[required, fullName]}
              >
                <Input placeholder={t('first_name')} />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item
                label={t('identification_number')}
                name={[field.name, 'identification_number']}
                rules={[
                  required,
                  minLength,
                  {
                    validator: (_, value) => {
                      const duplicateExists =
                        persons?.filter((person) => {
                          if (!person?.identification_number) return false
                          const otherIdnp = String(
                            person?.identification_number
                          ).trim()

                          return otherIdnp === value
                        }).length > 1

                      if (duplicateExists)
                        return Promise.reject(t('duplicate_idnp_error'))

                      return Promise.resolve()
                    }
                  }
                ]}
              >
                <Input placeholder={t('identification_number')} />
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item
                label={t('date_of_birth')}
                name={[field.name, 'date_of_birth']}
                rules={[required]}
              >
                <DatePickerWrapper disabled={fiftyFiftyFull} />
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item
                label={t('driving_internship')}
                name={[field.name, 'experience']}
                dependencies={[insurancePackageProps.name]}
                rules={[
                  required,
                  ...(isMini
                    ? [
                        {
                          validator: (_: unknown, value: number) => {
                            if (value === undefined || value === null)
                              return Promise.resolve()

                            if (value < MIN_MINI_DRIVER_EXPERIENCE)
                              return Promise.reject(
                                new Error(
                                  t('minimum_experience_required', {
                                    number: MIN_MINI_DRIVER_EXPERIENCE
                                  })
                                )
                              )

                            return Promise.resolve()
                          }
                        }
                      ]
                    : [])
                ]}
              >
                <ExperienceWrapper disabled={fiftyFiftyFull} />
              </Form.Item>
            </Col>
          </Row>
        )}
      </EditPersonsWrapper>
    </Flex>
  )
}
